import { apiService } from "@/services";
import { useMutation } from "@tanstack/react-query";
import queryClient from "@/query-client";
import { handleApiError } from "@/utils";
import { AddHeroProductKeywordsParams } from "@/types";
import type { Keyword } from "@/types/keyword";
import { SimpleSourceTag } from "@/types/keyword";

export type AddHeroProductKeywordsResponse = {
  inserted: number;
  skipped_duplicates: number;
  product_id: number;
  keywords: { id: number; search: string }[];
};

export const getKeywordsQueryPrefix = (productId: number) =>
  ["product", "keywords", String(productId)] as const;

let optimisticKeywordIdCounter = -1;

type AddKeywordsMutationContext = {
  snapshots: Array<[readonly unknown[], Keyword[] | undefined]>;
};

const buildOptimisticKeyword = (search: string, productId: number): Keyword => ({
  id: optimisticKeywordIdCounter--,
  search,
  product_id: productId,
  rank: null,
  search_volume: null,
  search_volume_trend: null,
  kps: null,
  landscape_key: null,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  landscape_id: null,
  ppc: null,
  ppc_min_bid: null,
  ppc_max_bid: null,
  keyword_sales: null,
  asin_density: null,
  competitors: null,
  ranks: null,
  serp_match_pct: null,
  serp_match_organic_pct: null,
  serp_match_sponsored_pct: null,
  serp_match_scored: null,
  serp_match_found: null,
  serp_match_week: null,
  hero_relevance: null,
  includes_hero_brand: false,
  includes_competitor_brand: false,
  no_overlap_with_hero_content: false,
  kps_raw: null,
  kps_search_score: null,
  kps_search_trend_score: null,
  kps_hero_rank_score: null,
  hero_rank: null,
  kps_hero_relevance_score: null,
  kps_ppc_score: null,
  kps_asin_density_score: null,
  kps_keyword_sales_score: null,
  kps_max_hero_relevance_score: null,
  kps_max_search_score: null,
  kps_max_asin_density_score: null,
  kps_max_hero_rank_score: null,
  kps_max_search_trend_score: null,
  kps_max_keyword_sales_score: null,
  kps_hero_relevance_factor: null,
  hero_relevance_adjusted: null,
  rank_if_converted: null,
  individual_contribution: null,
  conversion: null,
  ranked_from_1_to_15: null,
  ranked_from_16_to_40: null,
  ranked_from_41_to_150: null,
  ranked_from_151: null,
  ranked_from_1: null,
  is_master_list: false,
  landscape: null,
  utilizationTags: [],
  utilization: [],
  sources: [SimpleSourceTag.MANUAL],
  sourceTags: [SimpleSourceTag.MANUAL],
});

export const useAddHeroProductKeywords = () => {
  return useMutation<AddHeroProductKeywordsResponse, unknown, AddHeroProductKeywordsParams>({
    mutationFn: (params) =>
      apiService.post<AddHeroProductKeywordsResponse, AddHeroProductKeywordsParams>(
        `/products/hero/${params.productId}/keywords`,
        { brand_id: params.brandId, keywords: params.keywords },
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["product", "detail"] });
      queryClient.invalidateQueries({ queryKey: getKeywordsQueryPrefix(variables.productId) });
    },
    onError: (error) => {
      handleApiError(error);
    },
  });
};

type DeleteKeywordMutationContext = {
  snapshots: Array<[readonly unknown[], Keyword[] | undefined]>;
};

export const useDeleteKeyword = () => {
  return useMutation<void, unknown, { productId: number; keywordId: number }, DeleteKeywordMutationContext>({
    mutationFn: ({ keywordId }) => apiService.delete<void, unknown>(`/keywords/${keywordId}`),
    onMutate: async (variables) => {
      const queryPrefix = getKeywordsQueryPrefix(variables.productId);
      await queryClient.cancelQueries({ queryKey: queryPrefix });

      const snapshots = queryClient.getQueriesData<Keyword[]>({
        queryKey: queryPrefix,
      });

      for (const [queryKey, prev] of snapshots) {
        queryClient.setQueryData<Keyword[] | undefined>(
          queryKey,
          prev ? prev.filter((k) => k.id !== variables.keywordId) : prev,
        );
      }

      return { snapshots };
    },
    onError: (_error, variables, context) => {
      if (!context) return;
      for (const [queryKey, snapshot] of context.snapshots) {
        queryClient.setQueryData(queryKey, snapshot);
      }
      queryClient.invalidateQueries({ queryKey: ["product", "detail"] });
      queryClient.invalidateQueries({ queryKey: ["product", "keywords", String(variables.productId)] });
      handleApiError(_error);
    },
  });
};
